import { emptyFragment } from '@execonline-inc/execonline-ui';
import { parseIntM } from '@execonline-inc/numbers';
import { nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import { useEffect, useRef, useState } from 'react';
import { Params } from '.';
import Main from '../../BrowserSupport/Main';
import WithCurrentUser from '../../CurrentUser/Context/WithCurrentUser';
import { findLink } from '../../LinkyLinky';
import { lightTheme } from '../../Native/styles';
import NotificationSource from '../../NotificationSource';
import Notifications from '../../Notifications';
import { ProgramModuleProgressProvider } from '../../ProgramModuleProgress/Context';
import ProgramStore from '../../ProgramStore';
import SegmentStore from '../../SegmentStore';
import ThemeContext from '../../Themes/ThemeContext';
import SiteTitle from '../../Title';
import AutoLaunchable from '../AutoLaunchable';
import { Footer } from '../Footer';
import NavigationBar from '../Platform/NavigationBar';
import * as platformStyle from '../Platform/style.module.css';
import ProgramReactions from '../ProgramReactions';
import SegmentReactions from '../SegmentReactions';
import SkipNavLink from '../SkipNavLink';
import Toasts from '../Toasts';
import { T } from '../../Translations';
import { useTranslationsContext } from '../../Translations/UseTranslationsContext';
import CancelRegistration from './CancelRegistration';
import SegmentContent from './SegmentContent';
import SegmentHeader from './SegmentHeader';
import NextModuleLocked from './NextModuleLocked';
import SegmentNavigation from './SegmentNavigation';
import * as style from './style.module.css';

interface Props {
  segmentStore: SegmentStore;
  programStore: ProgramStore;
  params: Params;
}

function FullSegment({ segmentStore, programStore, params }: Props) {
  const { translate } = useTranslationsContext();
  const scrollEndSentinelRef = useRef<HTMLDivElement>(null);
  const stickyNavRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  // Latches once the end of content is reached so scrolling back up doesn't re-show the bubble.
  const [dismissed, setDismissed] = useState(false);
  // Content loads async; without this the empty first render would latch dismissed immediately.
  const hasSeenOverflowRef = useRef(false);

  useEffect(() => {
    setDismissed(false);
    hasSeenOverflowRef.current = false;
  }, [params.segmentId]);

  useEffect(() => {
    const check = () => {
      const sentinel = scrollEndSentinelRef.current;
      const nav = stickyNavRef.current;
      if (!sentinel || !nav) return;
      const sentinelBottom = sentinel.getBoundingClientRect().bottom;
      const visibleBottom = window.innerHeight - nav.offsetHeight;
      const overflow = sentinelBottom > visibleBottom;
      setHasOverflow(overflow);
      if (overflow) {
        hasSeenOverflowRef.current = true;
      } else if (hasSeenOverflowRef.current) {
        setDismissed(true);
      }
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    const ro = new ResizeObserver(check);
    ro.observe(document.documentElement);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      ro.disconnect();
    };
  }, [params.segmentId]);

  // Dismissal is left to the scroll listener so a click that falls short keeps the bubble.
  const scrollOneViewport = () => {
    const nav = stickyNavRef.current;
    if (!nav) return;
    const continuityOverlap = 36;
    const delta = window.innerHeight - nav.offsetHeight - continuityOverlap;
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const target = Math.min(window.scrollY + delta, maxScroll);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
    window.scrollTo({ top: target, behavior });
  };

  return (
    <>
      {segmentStore.title.map((t) => <SiteTitle title={t} />).getOrElse(emptyFragment)}
      <div className={platformStyle.containerFull}>
        <SkipNavLink />
        <Toasts teamsStore={nothing()} />
        <NavigationBar />
      </div>
      <div className="flex">
        <div id="segment-column" className={`${platformStyle.container} mx-auto`}>
          <Notifications />
          <AutoLaunchable />
          <Main id="main" className={style.container}>
            <ThemeContext.Provider value={lightTheme}>
              <ProgramModuleProgressProvider store={segmentStore}>
                <SegmentHeader segmentStore={segmentStore} programStore={programStore} />
                <div className={style.content}>
                  {segmentStore.nextUnavailable || segmentStore.segmentUnavailable ? (
                    <NextModuleLocked
                      segmentStore={segmentStore}
                      programStore={programStore}
                      programId={parseIntM(params.programId)}
                      openedModuleId={
                        segmentStore.segmentUnavailable ? parseIntM(params.moduleId) : nothing()
                      }
                    />
                  ) : (
                    <SegmentContent store={segmentStore} />
                  )}
                  <NotificationSource store={segmentStore} />
                </div>
                <div ref={scrollEndSentinelRef} aria-hidden="true" className="h-px w-full" />
                <div
                  ref={stickyNavRef}
                  data-segment-sticky-nav
                  className="sticky bottom-0 z-30 border-t border-gray-200 bg-white px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] sm:px-6"
                >
                  {hasOverflow && !dismissed && (
                    <>
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-x-0 bottom-full h-12 bg-gradient-to-t from-white to-transparent"
                      />
                      <div className="absolute inset-x-0 bottom-full flex justify-center pb-2">
                        <button
                          type="button"
                          onClick={scrollOneViewport}
                          aria-label={translate('Scroll down to see more')}
                          data-intercom-target="segment-scroll-more-button"
                          className="flex items-center gap-1.5 rounded-full bg-teal-600 px-4 py-1.5 text-sm font-medium text-white shadow-md transition-colors hover:bg-teal-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
                        >
                          <span>
                            <T kind="More" />
                          </span>
                          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </>
                  )}
                  <div className="flex flex-col gap-x-4 gap-y-2 sm:flex-row">
                    {findLink('delete', programStore.programLinks)
                      .map((l) => <CancelRegistration cancelLink={l} programStore={programStore} />)
                      .getOrElse(emptyFragment)}
                    <SegmentNavigation store={segmentStore} programStore={programStore} />
                  </div>
                </div>
              </ProgramModuleProgressProvider>
            </ThemeContext.Provider>
          </Main>

          <Footer />
        </div>
        <SegmentReactions
          store={segmentStore}
          params={params}
          advanceHook={() => {}}
          fireImmediately={true}
        />
        <WithCurrentUser
          children={(currentUserResource) => (
            <ProgramReactions store={programStore} currentUserResource={currentUserResource} />
          )}
        />
      </div>
    </>
  );
}
export default observer(FullSegment);
